import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";

import dayjs from "dayjs";
import { getBrandListing } from "../../../api/context/brand";
import { getManufacturesListingPaginate } from "../../../api/context/manufacturer";

const OrderFilterModal = ({ show, onHide, onApplyFilters, searchFormik, filterData, userType }) => {
  const [activeFilter, setActiveFilter] = useState("Order Status");
  const isAdmin = userType === 'ADMIN';

  const [searchQueries, setSearchQueries] = useState({
    "Order Status": "",
    "Payment Mode": "",
    "Payment Status": "",
    Brands: "",
    Vendors: "",
  });

  const [localSelections, setLocalSelections] = useState({
    orderStatus: [],
    paymentMode: [],
    paymentStatus: [],
    brands: [],
    vendors: [],
    startDate: "",
    endDate: "",
  });

  const [brandList, setBrandList] = useState([]);
  const [vendorList, setVendorList] = useState([]);

  const orderStatusOptions = [
    { id: "INITIATED", name: "INITIATED" },
    { id: "CONFIRMED", name: "CONFIRMED" },
    { id: "DELIVERED", name: "DELIVERED" },
    { id: "CANCELLED", name: "CANCELLED" },
    { id: "REJECTED", name: "REJECTED" },
  ];

  const paymentModeOptions = [
    { id: "COD", name: "COD" },
    { id: "ONLINE", name: "ONLINE" },
  ];

  const paymentStatusOptions = [
    { id: "PENDING", name: "PENDING" },
    { id: "DONE", name: "DONE" },
  ];

  const filterTypes = [
    { key: "Order Status", stateKey: "orderStatus" },
    { key: "Payment Mode", stateKey: "paymentMode" },
    { key: "Payment Status", stateKey: "paymentStatus" },
    ...(isAdmin ? [{ key: "Brands", stateKey: "brands" }] : []),
    ...(isAdmin ? [{ key: "Vendors", stateKey: "vendors" }] : []),
    { key: "Date Range", stateKey: "date" },
  ];

  useEffect(() => {
    if (show) {
      if (isAdmin) {
        fetchBrands();
        fetchVendors();
      }

      setLocalSelections({
        orderStatus: filterData?.orderStatus
          ? [{ id: filterData.orderStatus, name: filterData.orderStatus }]
          : [],
        paymentMode: filterData?.paymentMode
          ? [{ id: filterData.paymentMode, name: filterData.paymentMode }]
          : [],
        paymentStatus: filterData?.paymentStatus
          ? [{ id: filterData.paymentStatus, name: filterData.paymentStatus }]
          : [],
        brands: (filterData?.brands || []).map((id) => ({ id })),
        vendors: (filterData?.vendors || []).map((id) => ({ id })),
        startDate: filterData?.startDate || "",
        endDate: filterData?.endDate || "",
      });
    }
  }, [show, filterData, isAdmin]);

  const fetchBrands = () => {
    getBrandListing({ page: 1, pageSize: 100 })((res) => {
      console.log("Brand API Response:", res); // 🔍 DEBUG
      if (res?.statusCode === 200 && res?.data) {
        setBrandList(res.data.map((b) => ({ id: b.id, name: b.name })));
      } else {
        console.log("Brand API Error or Empty:", res);
        setBrandList([]);
      }
    });
  };

  const fetchVendors = () => {
    getManufacturesListingPaginate({ page: 1, limit: 100, search: "" })((res) => {
      console.log("Vendor API Response:", res); // 🔍 DEBUG: Check API response
      if (res?.statusCode === 200 && res?.results) {
        const vendors = res.results.map((v) => ({
          id: v.value || v.id,
          name: v.label || v.storeName
        }));
        console.log("Mapped Vendors:", vendors); // 🔍 DEBUG: Check mapped data
        setVendorList(vendors);
      } else {
        console.log("Vendor API Error or Empty:", res);
        setVendorList([]);
      }
    });
  };

  // Single selection for Order Status, Payment Mode, Payment Status
  const singleSelectFilters = ["Order Status", "Payment Mode", "Payment Status"];
  const isMultiSelect = !singleSelectFilters.includes(activeFilter);

  const handleSelection = (key, item) => {
    if (isMultiSelect) {
      // Multiple selection (checkbox) - toggle behavior
      const exists = localSelections[key].some((x) => x.id === item.id);
      setLocalSelections((prev) => ({
        ...prev,
        [key]: exists
          ? prev[key].filter((x) => x.id !== item.id)
          : [...prev[key], item],
      }));
    } else {
      // Single selection (radio) - replace all with one
      setLocalSelections((prev) => ({
        ...prev,
        [key]: [item],
      }));
    }
  };

  const getList = () => {
    switch (activeFilter) {
      case "Order Status": return orderStatusOptions;
      case "Payment Mode": return paymentModeOptions;
      case "Payment Status": return paymentStatusOptions;
      case "Brands": return brandList;
      case "Vendors": return vendorList;
      default: return [];
    }
  };

  const handleApply = () => {
    const filters = {
      orderStatus: localSelections.orderStatus.map((i) => i.id)[0] || "",
      paymentMode: localSelections.paymentMode.map((i) => i.id)[0] || "",
      paymentStatus: localSelections.paymentStatus.map((i) => i.id)[0] || "",
      startDate: localSelections.startDate,
      endDate: localSelections.endDate,
    };

    if (isAdmin) {
      filters.brands = localSelections.brands.map((i) => i.id);
      filters.vendors = localSelections.vendors.map((i) => i.id);
    }

    onApplyFilters(filters);

    onHide();
  };

  const handleClear = () => {
    setLocalSelections({
      orderStatus: [],
      paymentMode: [],
      paymentStatus: [],
      brands: [],
      vendors: [],
      startDate: "",
      endDate: "",
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Filters</Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        <div className="d-flex" style={{ minHeight: 400 }}>

          {/* LEFT */}
          <div className="border-end bg-light" style={{ width: 220 }}>
            {filterTypes.map((f) => (
              <div
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`p-3 ${activeFilter === f.key ? "bg-white fw-bold" : ""}`}
                style={{ cursor: "pointer" }}
              >
                {f.key}
              </div>
            ))}
          </div>

          {/* RIGHT */}
          <div className="p-3 flex-grow-1">

            {activeFilter === "Date Range" ? (
              <>
                <h5 className="mb-4 fw-bold">Select Date</h5>
                <div className="mb-3">
                  <label className="form-label fw-bold text-secondary mb-2">Start Date</label>
                  <Form.Control
                    type="date"
                    className="mb-2"
                    value={localSelections.startDate}
                    onChange={(e) =>
                      setLocalSelections((p) => ({ ...p, startDate: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="form-label fw-bold text-secondary mb-2">End Date</label>
                  <Form.Control
                    type="date"
                    value={localSelections.endDate}
                    onChange={(e) =>
                      setLocalSelections((p) => ({ ...p, endDate: e.target.value }))
                    }
                  />
                </div>
              </>
            ) : (
              <>
                <Form.Control
                  placeholder="Search..."
                  className="mb-3"
                  value={searchQueries[activeFilter]}
                  onChange={(e) =>
                    setSearchQueries((p) => ({
                      ...p,
                      [activeFilter]: e.target.value,
                    }))
                  }
                />

                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  {getList()
                    .filter((i) =>
                      i.name.toLowerCase().includes(searchQueries[activeFilter].toLowerCase())
                    )
                    .map((item) => {
                      const key = filterTypes.find(f => f.key === activeFilter).stateKey;
                      const checked = localSelections[key].some((x) => x.id === item.id);

                      return (
                        <div
                          key={item.id}
                          onClick={() => handleSelection(key, item)}
                          style={{
                            padding: 10,
                            cursor: "pointer",
                            background: checked ? "#eef2f7" : "",
                          }}
                        >
                          <input
                            type={isMultiSelect ? "checkbox" : "radio"}
                            name={isMultiSelect ? "" : key}
                            checked={checked}
                            readOnly
                            style={{ marginRight: "10px", cursor: "pointer" }}
                          />
                          {item.name}
                        </div>
                      );
                    })}
                </div>
              </>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="link" onClick={handleClear}>Clear</Button>
        <Button className="request_btn" onClick={handleApply}>
          Apply Filters
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OrderFilterModal;
